import apiClient from "../utils/apiClient";
import { apiUtils } from "../utils/commonUtils";

// Get all users
export const fetchUsers = async (filters = {}) => {
  try {
    const { search = "", status = "", is_active, sort_by = "", per_page = 15 } = filters;

    const params = {};

    if (search) params.search = search;
    if (is_active !== undefined && is_active !== null) {
      if (typeof is_active === "boolean") {
        params.is_active = is_active ? "1" : "0";
      } else if (typeof is_active === "number") {
        params.is_active = is_active === 1 ? "1" : "0";
      } else {
        params.is_active = is_active;
      }
    }
    
    if (status !== "" && status !== undefined && status !== null) {
      if (typeof status === "boolean") {
        params.status = status ? "1" : "0"; // Try "1"/"0" format
      } else {
        params.status = status;
      }
    }
    
    if (sort_by) params.sort_by = sort_by;
    if (per_page) params.per_page = per_page;

    const response = await apiClient.get("/users", { params });

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        meta: response.data.meta,
        message: response.data.message,
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to fetch users");
  }
};

// Create new user
export const createUser = async (userData) => {
  try {
    const response = await apiClient.post("/users", {
      name: userData.name,
      email: userData.email,
      password: userData.password,
      position: userData.position || null,
      phone: userData.phone || null,
    });

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User created successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to create user");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to create user");
  }
};

// Get user by ID
export const getUserById = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}`);

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch user");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to fetch user");
  }
};

// Activate user
export const activateUser = async (userId) => {
  try {
    const response = await apiClient.post(`/users/${userId}/activate`);

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User activated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to activate user");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to activate user");
  }
};

// Deactivate user
export const deactivateUser = async (userId) => {
  try {
    const response = await apiClient.post(`/users/${userId}/deactivate`);

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User deactivated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to deactivate user");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to deactivate user");
  }
};

// Update user
export const updateUser = async (userId, userData) => {
  try {
    const { name, email, password, position, phone } = userData;

    const updateData = {};
    if (name) updateData.name = name;
    if (email) updateData.email = email;
    if (password) updateData.password = password;
    updateData.position = position || null;
    updateData.phone = phone || null;
    const response = await apiClient.put(`/users/${userId}`, updateData);

    if (response.data.status === "success") {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "User updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update user");
    }
  } catch (error) {
    return apiUtils.handleError(error, "Failed to update user");
  }
};

// Delete user
export const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`/users/${userId}`);

    if (response.status >= 200 && response.status < 300) {
      return {
        success: true,
        message: response.data?.message || "User deleted successfully",
      };
    }

    throw new Error(response.data?.message || "Failed to delete user");
  } catch (error) {
    return apiUtils.handleError(error, "Failed to delete user");
  }
};

export const transformUserData = (apiUser) => {
  return {
    id: apiUser.id,
    name: apiUser.name,
    email: apiUser.email,
    status: apiUser.status || "active",
    createdAt: apiUser.created_at,
    updatedAt: apiUser.updated_at,
  };
};

export const transformUsersData = (apiUsers) => {
  return apiUsers.map(transformUserData);
};
